import { useEffect, useState } from 'react';
import { DashboardPage } from './pages/DashboardPage';
import { LoginPage } from './pages/LoginPage';

export type AdminUser = {
  email: string;
  displayName: string;
  role: string;
};

type AuthState = 'checking' | 'anonymous' | 'authenticated';

async function fetchCurrentUser() {
  const response = await fetch('/api/admin/session', {
    credentials: 'same-origin'
  });

  if (!response.ok) {
    return null;
  }

  const json = (await response.json()) as { user?: AdminUser };

  return json.user ?? null;
}

export function AdminApp() {
  const [authState, setAuthState] = useState<AuthState>('checking');
  const [user, setUser] = useState<AdminUser | null>(null);

  useEffect(() => {
    let isMounted = true;

    fetchCurrentUser()
      .then((currentUser) => {
        if (!isMounted) {
          return;
        }

        setUser(currentUser);
        setAuthState(currentUser ? 'authenticated' : 'anonymous');
      })
      .catch(() => {
        if (!isMounted) {
          return;
        }

        setUser(null);
        setAuthState('anonymous');
      });

    return () => {
      isMounted = false;
    };
  }, []);

  async function handleLogout() {
    await fetch('/api/admin/logout', {
      method: 'POST',
      credentials: 'same-origin'
    });
    setUser(null);
    setAuthState('anonymous');
  }

  function handleAuthenticated(nextUser: AdminUser) {
    setUser(nextUser);
    setAuthState('authenticated');
  }

  return (
    <main className="admin-shell admin-shrine-shell" data-testid="admin-shrine-shell">
      <div className="admin-grain" aria-hidden="true" />
      <header className="admin-header admin-shrine-header">
        <a className="admin-brand" href="/" aria-label="Kyoreido Chronicles home">
          <span className="admin-seal">虚鈴道</span>
          <span>
            <strong>Kyoreido Chronicles</strong>
            <small>Legend of Mugen</small>
          </span>
        </a>
        <span className="admin-session">{user ? user.displayName : 'Cloudflare Pages + D1'}</span>
      </header>
      <section className="admin-hero">
        <p className="eyebrow">Shrine Ledger</p>
        <h1>Kyoreido Chronicles Admin</h1>
        <p>
          A shrine ledger for road-test messages, account security, and the records behind the old roads.
        </p>
      </section>
      {authState === 'checking' ? <section className="admin-panel login-panel">Checking session...</section> : null}
      {authState === 'anonymous' ? <LoginPage onAuthenticated={handleAuthenticated} /> : null}
      {authState === 'authenticated' && user ? <DashboardPage user={user} onLogout={handleLogout} /> : null}
    </main>
  );
}
